import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import axiosInstance from '../../apiServices/axios';
import { PULSE_PREFERENCES_DETAIL } from '../../apiServices/api_routes';
import styles from './css/UserSettings.module.css';

const UserPulseEmailPreferences = () => {
    const activeWorkspaceId = useSelector(state => state.user?.activeWorkspaceId);
    const [dailyPulseEmail, setDailyPulseEmail] = useState(true);
    const [savedDailyPulseEmail, setSavedDailyPulseEmail] = useState(true);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const hasChanges = dailyPulseEmail !== savedDailyPulseEmail;

    useEffect(() => {
        if (!activeWorkspaceId) return;
        let cancelled = false;

        const loadPreferences = async () => {
            setLoading(true);
            try {
                const { data } = await axiosInstance.get(PULSE_PREFERENCES_DETAIL(activeWorkspaceId));
                if (cancelled) return;
                const enabled = data?.email_enabled ?? true;
                setDailyPulseEmail(enabled);
                setSavedDailyPulseEmail(enabled);
            } catch (error) {
                if (cancelled) return;
                const msg = error?.response?.data?.detail || 'Failed to load email preferences';
                toast.error(typeof msg === 'string' ? msg : 'Failed to load email preferences');
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        loadPreferences();
        return () => {
            cancelled = true;
        };
    }, [activeWorkspaceId]);

    const handleToggle = () => {
        if (loading || saving) return;
        setDailyPulseEmail(prev => !prev);
    };

    const handleSave = async () => {
        if (!activeWorkspaceId || saving || !hasChanges) return;

        setSaving(true);
        try {
            const { data } = await axiosInstance.patch(PULSE_PREFERENCES_DETAIL(activeWorkspaceId), {
                email_enabled: dailyPulseEmail,
            });
            const enabled = data?.email_enabled ?? dailyPulseEmail;
            setDailyPulseEmail(enabled);
            setSavedDailyPulseEmail(enabled);
            toast.success('Email preferences updated');
        } catch (error) {
            const msg = error?.response?.data?.detail || 'Failed to update email preferences';
            toast.error(typeof msg === 'string' ? msg : 'Failed to update email preferences');
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <header className={styles.pageHeader}>
                <h1 className={styles.pageTitle}>Email Preferences</h1>
                <p className={styles.pageDescription}>Choose which Lens email updates you&apos;d like to receive.</p>
            </header>

            {!activeWorkspaceId ? (
                <section className={styles.card}>
                    <div className={styles.emailPreferenceCardBody}>
                        <p className={styles.emptyStateText}>Select a workspace to manage email preferences.</p>
                    </div>
                </section>
            ) : (
                <>
                    <section className={styles.card}>
                        <div className={styles.emailPreferenceCardBody}>
                            <div className={styles.emailPreferenceMain}>
                                <p className={styles.emailPreferenceLabel}>Daily Lens Pulse Email</p>
                                <p className={styles.emailPreferenceDescription}>
                                    Sent every weekday at 8:00 AM GMT highlighting the most important priorities, risks, decisions and
                                    delivery signals across your projects.
                                </p>
                            </div>
                            <div className={styles.emailPreferenceControl}>
                                <span className={styles.emailPreferenceStatus}>{dailyPulseEmail ? 'Enabled' : 'Disabled'}</span>
                                <button
                                    type="button"
                                    role="switch"
                                    aria-checked={dailyPulseEmail}
                                    aria-label="Daily Lens Pulse Email"
                                    className={`${styles.toggleSwitch} ${dailyPulseEmail ? styles.toggleSwitchOn : ''}`}
                                    onClick={handleToggle}
                                    disabled={loading || saving}
                                />
                            </div>
                        </div>
                    </section>

                    <div className={styles.pageActions}>
                        <button
                            type="button"
                            className={styles.primaryButton}
                            disabled={!hasChanges || saving || loading}
                            onClick={handleSave}>
                            {saving ? 'Saving…' : 'Save Changes'}
                        </button>
                    </div>
                </>
            )}
        </>
    );
};

export default UserPulseEmailPreferences;
